import { useMemo, useState } from 'react'
import { Alert, Box, Grid } from '@mui/material'
import { BarChart } from '@mui/icons-material'

import {
    useGetBreakdownQuery,
    useGetOverviewQuery,
    useGetProjectConfigQuery,
    useGetTimeseriesQuery,
} from '@/apis/v2Api'
import { exactEventsForRange, fillDailyGaps, mergeSeries } from '@/apis/v2Mappers'
import { useReportArgs } from '@/hooks/useReportArgs'
import { granularityForPeriod, type Period } from '@/utils/dates'
import {
    BreakdownTable,
    Kpi,
    LineChartCard,
    ReportHeader,
    ReportStatus,
    SectionCard,
    fmtMoney,
    fmtMs,
    fmtNum,
    fmtPct,
} from '@/modules/reports/common'

export function OverviewPage() {
    const [period, setPeriod] = useState<Period>('7d')
    const args = useReportArgs(period)
    const interval = granularityForPeriod(period)

    const overview = useGetOverviewQuery(args)
    const { data: config } = useGetProjectConfigQuery(args.projectId)
    const pageviews = useGetTimeseriesQuery({ ...args, metric: 'pageviews', interval })
    const visitors = useGetTimeseriesQuery({ ...args, metric: 'visitors', interval })
    const topPages = useGetBreakdownQuery({ ...args, dim: 'path', event: 'page_view', limit: 10 })
    const channels = useGetBreakdownQuery({ ...args, dim: 'channel', limit: 10 })

    const chartRows = useMemo(
        () =>
            fillDailyGaps(
                mergeSeries({ pageviews: pageviews.data, visitors: visitors.data }),
                interval,
                args.from,
                args.to,
                ['pageviews', 'visitors'],
            ),
        [pageviews.data, visitors.data, interval, args.from, args.to],
    )
    const exact = exactEventsForRange(config?.config, ['purchase', 'generate_lead'], args.from)
    const currency = config?.config.currency ?? 'USD'
    const k = overview.data?.kpis
    const ch = overview.data?.change ?? {}
    const hasCommerce = !!k && ((k.purchases ?? 0) > 0 || (k.leads ?? 0) > 0)

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader title="Overview" period={period} onPeriodChange={setPeriod} />
            <ReportStatus
                isLoading={overview.isLoading}
                error={overview.error}
                isEmpty={!!k && !k.events}
                emptyIcon={<BarChart />}
                emptyTitle="No events in this range"
                emptyDescription="Install the YOM SDK (see Integration Guide) or pick a longer range. Events show up here within a minute of being collected."
            >
                {overview.data?.source === 'rollup' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This range reaches past the raw-event window, so it is served from daily
                        rollups: session-level metrics (bounce rate, session length) are not
                        available.
                    </Alert>
                )}
                {k && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Pageviews"
                                value={fmtNum(k.pageviews)}
                                change={ch.pageviews}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Visitors"
                                value={fmtNum(k.visitors)}
                                change={ch.visitors}
                                estimate={overview.data?.estimate}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Sessions"
                                value={fmtNum(k.sessions)}
                                change={ch.sessions}
                                estimate={overview.data?.estimate}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Bounce rate"
                                value={fmtPct(k.bounce_rate)}
                                change={ch.bounce_rate}
                                invert
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi title="Events" value={fmtNum(k.events)} change={ch.events} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Avg. session"
                                value={fmtMs(k.avg_session_ms)}
                                change={ch.avg_session_ms}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Pages / session"
                                value={fmtNum(k.pages_per_session, 2)}
                                change={ch.pages_per_session}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Kpi
                                title="Errors"
                                value={fmtNum(k.errors)}
                                change={ch.errors}
                                invert
                            />
                        </Grid>
                        {hasCommerce && (
                            <>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Purchases"
                                        value={fmtNum(k.purchases)}
                                        change={ch.purchases}
                                        exact={exact.has('purchase')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Revenue"
                                        value={fmtMoney(k.revenue, currency)}
                                        change={ch.revenue}
                                        exact={exact.has('purchase')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Conversion rate"
                                        value={fmtPct(k.conversion_rate)}
                                        change={ch.conversion_rate}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Kpi
                                        title="Leads"
                                        value={fmtNum(k.leads)}
                                        change={ch.leads}
                                        exact={exact.has('generate_lead')}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                )}

                <Box sx={{ mb: 3 }}>
                    <LineChartCard
                        title="Traffic over time"
                        rows={chartRows}
                        interval={interval}
                        series={[
                            { key: 'pageviews', label: 'Pageviews' },
                            { key: 'visitors', label: 'Visitors' },
                        ]}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SectionCard title="Top pages">
                            <ReportStatus
                                isLoading={topPages.isLoading}
                                error={topPages.error}
                                isEmpty={(topPages.data ?? []).length === 0}
                                emptyTitle="No page views yet"
                            >
                                <BreakdownTable
                                    rows={topPages.data ?? []}
                                    keyLabel="Page"
                                    countLabel="Views"
                                />
                            </ReportStatus>
                        </SectionCard>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SectionCard title="Channels">
                            <ReportStatus
                                isLoading={channels.isLoading}
                                error={channels.error}
                                isEmpty={(channels.data ?? []).length === 0}
                                emptyTitle="No traffic yet"
                            >
                                <BreakdownTable rows={channels.data ?? []} keyLabel="Channel" />
                            </ReportStatus>
                        </SectionCard>
                    </Grid>
                </Grid>
            </ReportStatus>
        </Box>
    )
}
